import ApiContext from 'api/ApiContext';
import { CountingCirclesList, CountingCirclesResponse } from 'api/api.types';
import { FormattedCandidate } from 'lib/CandidatePortraits/CandidatePortraits';
import { useContext } from 'react';
import getCandidatesIncludingEmptySeats from 'utils/getCandidatesIncludingEmptySeats';

interface Options {
  isBiProportional?: boolean;
  showOnlyElected?: boolean;
  addEmptySeats?: boolean;
}

const useGetCandidatesCanton = (
  aggregatingDomainsData: CountingCirclesResponse,
  options?: Options,
): FormattedCandidate[] => {
  const { language } = useContext(ApiContext);
  const {
    isBiProportional = false,
    showOnlyElected = false,
    addEmptySeats = false,
  } = options ?? {};

  if (!isBiProportional) {
    return null;
  }

  const districtsPlusOpenSeats = addEmptySeats
    ? aggregatingDomainsData?.data?.records?.map((record) => {
        if (
          record.seat_count ===
          record.candidates.filter((candidate) => candidate.results.elected).length
        ) {
          return record;
        }

        const partiesWithEmptySeats = record.lists
          .map((list) => {
            const electedCandidates = record.candidates.filter(
              (candidate) =>
                candidate.results.elected &&
                (candidate.list_id === list.id || candidate.list_nr === list.list_number),
            ).length;

            const seatClaim = list?.results?.seat_claim || 0;

            if (electedCandidates === seatClaim) {
              return {
                listId: list.id,
                listNr: list.list_number,
                emptySeats: 0,
              };
            }

            return {
              listId: list.id,
              listNr: list.list_number,
              emptySeats: seatClaim - electedCandidates,
            };
          })
          .filter((emptySeats) => emptySeats.emptySeats > 0);

        const newCandidates = getCandidatesIncludingEmptySeats(
          record.candidates,
          partiesWithEmptySeats,
        );

        const recordWithEmptySeats = {
          ...record,
          candidates: newCandidates,
        };

        return recordWithEmptySeats;
      })
    : aggregatingDomainsData?.data?.records;
  // aggregatingDomainsData contains the list of elected candidates for each counting circle.
  // A cumulation generates the full list
  return districtsPlusOpenSeats?.flatMap((countingCircle) => {
    const listsMap: { [id: number]: CountingCirclesList } = countingCircle.lists.reduce(
      (map, list) => {
        return {
          ...map,
          [list.id]: list,
        };
      },
      {},
    );
    return countingCircle.candidates
      ?.filter((candidate) => {
        if (showOnlyElected) {
          return candidate.results.elected;
        }
        return true;
      })
      ?.map((candidate) => {
        return {
          id: candidate.id,
          candidateNumber: candidate.number,
          firstName: candidate.first_name,
          lastName: candidate.last_name,
          inOfficeSince: candidate.in_office_since_year,
          isUpToNow: candidate.is_up_to_now,
          party: listsMap[candidate.list_id].short_title[language],
          isArchiveCandidate: candidate.is_archive_candidate,
          location: candidate.city,
          votes: candidate.results?.votes || 0,
          list: listsMap[candidate.list_id].list_number,
          listName: listsMap[candidate.list_id].short_title[language],
          gender: candidate.gender,
          elected: candidate.results?.elected,
          photoPath: candidate.photo_path,
          countingCircle: countingCircle.name[language],
          bfsNumber: countingCircle.bfs_number,
          partyColor: listsMap[candidate.list_id].color,
        };
      });
  });
};

export default useGetCandidatesCanton;
